/**
 * AdminClientiPage — F-012 Phase 3 (IMPL-P3-010).
 *
 * Admin landing for fido management: a searchable list of business accounts
 * (AGENTE + COMMERCIALISTA) with their wallet stats. Clicking a row navigates
 * to the per-account detail page.
 *
 * Structure (UI pattern: guard + layout + testable inner):
 *   AdminClientiPage         → RequireAuth roles ADMIN + AppLayout +
 *                              feature-local QueryClientProvider
 *     AdminClientiPageInner  → search state + useClienti + render (exported for
 *                              isolated tests without the guard/provider shell)
 */
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { adminQueryClient } from '../lib/query-client';
import { errorMessageFor } from '../lib/admin-client';
import { useClienti } from '../hooks/useClienti';
import { AdminClientiSearchBar } from '../components/AdminClientiSearchBar';
import { AdminClientiTable } from '../components/AdminClientiTable';

export function AdminClientiPage() {
  return (
    <RequireAuth roles={['ADMIN']}>
      <AppLayout>
        <QueryClientProvider client={adminQueryClient}>
          <AdminClientiPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function AdminClientiPageInner() {
  const [search, setSearch] = useState('');
  const query = useClienti(search ? { search } : {});

  const items = query.data?.items ?? [];

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Gestione Fido</h1>
        <p className="text-muted-foreground">
          Imposta e modifica il fido degli utenti business (agenti e commercialisti).
        </p>
      </header>

      <AdminClientiSearchBar
        value={search}
        onChange={setSearch}
        isSearching={query.isFetching}
      />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(query.error)}</AlertDescription>
        </Alert>
      ) : null}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento clienti…
        </p>
      ) : null}

      {!query.isLoading && !query.isError && items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Nessun cliente trovato.
        </div>
      ) : null}

      {items.length > 0 ? <AdminClientiTable items={items} /> : null}
    </section>
  );
}
